import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Eye,
  EyeOff,
  Globe,
  Info,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  RefreshCw,
  Save,
  Send,
  Server,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Zap,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { mailApi } from "../../services/mailApi";
import ToggleSwitch from "../common/ToggleSwitch";

function useMailPerms() {
  const { permissions = [], user } = useAuth() || {};
  const has = (k) => permissions?.includes(k);
  return {
    canView: has("mail_settings:view") || has("mail_settings:manage"),
    canManage: has("mail_settings:manage"),
    userEmail: user?.email || user?.Email || "",
  };
}

const SMTP_PRESETS = [
  {
    id: "custom",
    label: "Kurumsal / Özel SMTP",
    subtitle: "Kendi mail sunucunuz",
    host: "",
    port: 587,
    useSsl: true,
    tip: "Kurumsal e-posta sunucunuzun (Exchange, cPanel, Zimbra vb.) SMTP adresini ve portunu girin.",
  },
  {
    id: "m365",
    label: "Microsoft 365 / Outlook",
    subtitle: "smtp.office365.com:587",
    host: "smtp.office365.com",
    port: 587,
    useSsl: true,
    tip: "Microsoft 365 yönetim panelinde gönderici hesap için 'Authenticated SMTP' açık olmalı ve MFA aktifse Uygulama Şifresi kullanılmalıdır.",
  },
  {
    id: "gmail",
    label: "Google Workspace / Gmail",
    subtitle: "smtp.gmail.com:587",
    host: "smtp.gmail.com",
    port: 587,
    useSsl: true,
    tip: "Google hesabınızda 2 Adımlı Doğrulama açık olmalı ve normal şifre yerine 16 haneli 'Uygulama Şifresi (App Password)' girilmelidir.",
  },
  {
    id: "yandex",
    label: "Yandex 360 Kurumsal",
    subtitle: "smtp.yandex.com:465",
    host: "smtp.yandex.com",
    port: 465,
    useSsl: true,
    tip: "Yandex Mail ayarlarından 'Uygulama Şifreleri' oluşturup Port 465 (SSL/TLS) ile kullanmanız önerilir.",
  },
  {
    id: "sendgrid",
    label: "SendGrid SMTP Relay",
    subtitle: "smtp.sendgrid.net:587",
    host: "smtp.sendgrid.net",
    port: 587,
    useSsl: true,
    tip: "SendGrid SMTP Relay kullanırken Kullanıcı Adı alanına 'apikey', Şifre alanına ise SendGrid API Anahtarınızı girin.",
  },
];

const ALLOWED_PORTS = [
  { port: 587, label: "587", badge: "STARTTLS (Önerilen)", ssl: true },
  { port: 465, label: "465", badge: "SSL/TLS", ssl: true },
  { port: 2525, label: "2525", badge: "Alternatif TLS", ssl: true },
  { port: 25, label: "25", badge: "Standart Relay", ssl: false },
];

const inputCls =
  "w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/80 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:bg-white dark:focus:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all disabled:opacity-60";

export default function MailSettingsTab() {
  const { firmCode } = useAuth() || {};
  const perms = useMailPerms();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hasPassword, setHasPassword] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [sysStatus, setSysStatus] = useState({ isEnabled: false, enabledFrom: null });
  const [sysLoading, setSysLoading] = useState(false);

  const [initialForm, setInitialForm] = useState(null);
  const [form, setForm] = useState({
    providerType: 1,
    fromName: "",
    fromEmail: "",
    host: "",
    port: 587,
    useSsl: true,
    username: "",
    password: "",
    isEnabled: false,
  });

  const [testTo, setTestTo] = useState("");
  const [testSubject, setTestSubject] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [lastTestResult, setLastTestResult] = useState(null);
  const [selectedPresetId, setSelectedPresetId] = useState("custom");
  const hostInputRef = useRef(null);

  const canEdit = perms.canManage;

  const activePreset = useMemo(() => {
    return SMTP_PRESETS.find((p) => p.id === selectedPresetId) || SMTP_PRESETS[0];
  }, [selectedPresetId]);

  const isDirty = useMemo(() => {
    if (!initialForm) return false;
    return (
      form.fromName !== initialForm.fromName ||
      form.fromEmail !== initialForm.fromEmail ||
      form.host !== initialForm.host ||
      Number(form.port) !== Number(initialForm.port) ||
      Boolean(form.useSsl) !== Boolean(initialForm.useSsl) ||
      form.username !== initialForm.username ||
      Boolean(form.isEnabled) !== Boolean(initialForm.isEnabled) ||
      Boolean(form.password && form.password.trim())
    );
  }, [form, initialForm]);

  const checklist = useMemo(() => {
    const hostOk = Boolean(form.host && form.host.trim().includes("."));
    const portOk = [25, 465, 587, 2525].includes(Number(form.port));
    const emailOk = Boolean(form.fromEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.fromEmail.trim()));
    const authOk = hasPassword || Boolean(form.password && form.password.trim());
    return [
      {
        id: "host",
        label: "SMTP Sunucu ve Port",
        detail: hostOk && portOk ? `${form.host.trim()}:${form.port}` : "Geçerli bir SMTP adresi ve port seçilmeli",
        ok: hostOk && portOk,
      },
      {
        id: "sender",
        label: "Gönderen E-posta Adresi",
        detail: emailOk ? form.fromEmail.trim() : "Geçerli bir gönderen e-posta adresi girilmeli",
        ok: emailOk,
      },
      {
        id: "auth",
        label: "Kimlik Doğrulama Şifresi",
        detail: hasPassword
          ? "Şifreli olarak kasada kayıtlı"
          : form.password?.trim()
          ? "Yeni şifre girildi (kaydedilmeyi bekliyor)"
          : "Henüz şifre / uygulama şifresi kaydedilmedi",
        ok: authOk,
      },
      {
        id: "status",
        label: "SMTP Gönderim Durumu",
        detail: form.isEnabled ? "Aktif (Abonelik ve sistem mailleri gönderilebilir)" : "Pasif (Sadece test maili gönderilebilir)",
        ok: Boolean(form.isEnabled),
      },
    ];
  }, [form, hasPassword]);

  async function load() {
    if (!perms.canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [dto, sys] = await Promise.all([
        mailApi.getSettings(),
        mailApi.getSystemStatus().catch(() => null),
      ]);

      if (sys) {
        setSysStatus({
          isEnabled: Boolean(sys.isEnabled ?? sys.IsEnabled),
          enabledFrom: sys.enabledFrom ?? sys.EnabledFrom ?? null,
        });
      }

      const loadedHasPwd = Boolean(dto?.hasPassword ?? dto?.HasPassword);
      setHasPassword(loadedHasPwd);
      setUpdatedAt(dto?.updatedAt ?? dto?.UpdatedAt ?? null);

      const cleanForm = {
        providerType: 1,
        fromName: dto?.fromName ?? dto?.FromName ?? "",
        fromEmail: dto?.fromEmail ?? dto?.FromEmail ?? "",
        host: dto?.host ?? dto?.Host ?? "",
        port: Number(dto?.port ?? dto?.Port ?? 587),
        useSsl: dto?.useSsl ?? dto?.UseSsl ?? true,
        username: dto?.username ?? dto?.Username ?? "",
        password: "",
        isEnabled: Boolean(dto?.isEnabled ?? dto?.IsEnabled ?? false),
      };

      setForm(cleanForm);
      setInitialForm(cleanForm);

      const matchedPreset = SMTP_PRESETS.find(
        (p) => p.host && p.host.toLowerCase() === (cleanForm.host || "").trim().toLowerCase()
      );
      setSelectedPresetId(matchedPreset ? matchedPreset.id : "custom");

      if (!testTo && ( perms.userEmail || cleanForm.fromEmail )) {
        setTestTo(perms.userEmail || cleanForm.fromEmail);
      }
    } catch (e) {
      setError(e?.message || "Mail ayarları yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [perms.canView]);

  function setField(key, value) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  function applyPreset(preset) {
    if (!canEdit) return;
    setSelectedPresetId(preset.id);

    if (preset.id === "custom") {
      const knownPresetHosts = SMTP_PRESETS.filter((x) => x.host).map((x) => x.host.toLowerCase());
      const currentHostLower = (form.host || "").trim().toLowerCase();
      const initialHost = (initialForm?.host || "").trim();
      const initialIsCustom = initialHost && !knownPresetHosts.includes(initialHost.toLowerCase());

      setForm((p) => ({
        ...p,
        host: knownPresetHosts.includes(currentHostLower)
          ? initialIsCustom
            ? initialHost
            : ""
          : p.host,
        port: preset.port,
        useSsl: preset.useSsl,
      }));

      setTimeout(() => {
        hostInputRef.current?.focus();
      }, 50);
      return;
    }

    setForm((p) => ({
      ...p,
      host: preset.host,
      port: preset.port,
      useSsl: preset.useSsl,
    }));
  }

  async function toggleAutoSystem() {
    if (!canEdit || sysLoading) return;
    setSysLoading(true);
    setError("");
    setSuccess("");
    try {
      if (sysStatus.isEnabled) {
        await mailApi.disableSystem();
        setSysStatus((p) => ({ ...p, isEnabled: false }));
        setSuccess("Otomatik mail zamanlayıcı sistemi devre dışı bırakıldı.");
      } else {
        const todayIso = new Date().toISOString().slice(0, 10);
        const res = await mailApi.enableSystem(todayIso);
        setSysStatus({
          isEnabled: true,
          enabledFrom: res?.enabledFrom || todayIso,
        });
        setSuccess("Otomatik mail zamanlayıcı sistemi aktif edildi.");
      }
    } catch (e) {
      setError(e?.message || "Otomatik mail sistemi durumu değiştirilemedi.");
    } finally {
      setSysLoading(false);
    }
  }

  async function saveSettingsInternal(silent = false) {
    if (!canEdit) return false;
    setSaving(true);
    if (!silent) {
      setError("");
      setSuccess("");
    }
    try {
      const payload = {
        providerType: 1,
        fromName: (form.fromName || "").trim(),
        fromEmail: (form.fromEmail || "").trim(),
        host: (form.host || "").trim(),
        port: Number(form.port),
        useSsl: Boolean(form.useSsl),
        username: (form.username || "").trim(),
        isEnabled: Boolean(form.isEnabled),
      };

      if (form.password && form.password.trim()) {
        payload.passwordPlain = form.password.trim();
      }

      await mailApi.upsertSettings(payload);
      if (!silent) {
        setSuccess("SMTP mail ayarları güvenli şekilde kaydedildi ve denetim loguna işlendi.");
      }
      setForm((p) => ({ ...p, password: "" }));
      await load();
      return true;
    } catch (e) {
      setError(e?.message || "Ayarlar kaydedilirken bir hata oluştu.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function save(e) {
    if (e) e.preventDefault();
    await saveSettingsInternal(false);
  }

  async function sendTest(e) {
    if (e) e.preventDefault();
    if (!canEdit) return;

    const targetEmail = (testTo || "").trim();
    if (!targetEmail) {
      setError("Test maili göndermek için lütfen bir alıcı e-posta adresi girin.");
      return;
    }

    setTestSending(true);
    setError("");
    setSuccess("");
    setLastTestResult(null);

    try {
      // Kullanıcı ayarları değiştirip henüz kaydetmediyse önce otomatik kaydet
      if (isDirty) {
        const savedOk = await saveSettingsInternal(true);
        if (!savedOk) {
          setTestSending(false);
          return;
        }
      }

      const res = await mailApi.testSettings({
        toEmail: targetEmail,
        subject: testSubject.trim() || undefined,
        message: testMessage.trim() || undefined,
      });

      const msg = res?.message || `Test e-postası ${targetEmail} adresine başarıyla gönderildi.`;
      setSuccess(msg);
      setLastTestResult({
        ok: true,
        time: new Date().toLocaleTimeString("tr-TR"),
        to: targetEmail,
        message: msg,
      });
    } catch (e) {
      const msg =
        e?.message ||
        e?.error ||
        e?.detail ||
        "Test maili gönderilemedi. Lütfen SMTP bilgilerini kontrol edin.";
      setError(msg);
      setLastTestResult({
        ok: false,
        time: new Date().toLocaleTimeString("tr-TR"),
        to: targetEmail,
        message: msg,
      });
    } finally {
      setTestSending(false);
    }
  }

  if (!perms.canView) {
    return (
      <div className="p-8 rounded-2xl border border-amber-200 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-900/10 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
          <ShieldAlert className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">Yetki Gerekli</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
            Mail sistem ayarlarını görüntülemek için <code className="font-mono text-xs bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded">mail_settings:view</code> yetkisine sahip olmalısınız.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Üst Başlık ve Hızlı Aksiyon Barı */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 shadow-sm border border-gray-100 dark:border-gray-700/80 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20 shrink-0">
            <Mail className="w-6 h-6" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                Mail Sistem Ayarları
              </h1>
              {firmCode && (
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200/60 dark:border-blue-800">
                  {firmCode}
                </span>
              )}
              {isDirty && (
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 animate-pulse">
                  Kaydedilmemiş Değişiklikler
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Rapor abonelikleri ve sistem bildirimleri için kurumsal SMTP sunucu yapılandırmasını ve güvenlik ayarlarını yönetin.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {updatedAt && (
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/60 px-3 py-2 rounded-xl border border-gray-200/70 dark:border-gray-700">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span>Son Kayıt: {new Date(updatedAt).toLocaleString("tr-TR")}</span>
            </div>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading || saving}
            className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/70 text-xs font-semibold text-gray-700 dark:text-gray-200 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Yenile
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={save}
              disabled={saving || loading}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs font-semibold shadow-sm shadow-blue-500/20 transition-all disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Ayarları Kaydet
            </button>
          )}
        </div>
      </div>

      {/* Durum Özeti Kartları (KPI) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* 1. SMTP Servis Durumu */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/80 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">SMTP Gönderim Durumu</span>
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  form.isEnabled ? "bg-emerald-500 animate-pulse" : "bg-gray-400"
                }`}
              />
              <span className="text-base font-bold text-gray-900 dark:text-white">
                {form.isEnabled ? "Aktif" : "Pasif"}
              </span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              {form.isEnabled ? "Mail çıkışına izin veriliyor" : "Canlı gönderimler kapalı"}
            </p>
          </div>
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center ${
              form.isEnabled
                ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                : "bg-gray-100 dark:bg-gray-700 text-gray-500"
            }`}
          >
            <Zap className="w-5 h-5" />
          </div>
        </div>

        {/* 2. Sunucu & Port */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/80 shadow-sm flex items-center justify-between">
          <div className="min-w-0 pr-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Tanımlı SMTP Sunucusu</span>
            <div className="text-base font-bold text-gray-900 dark:text-white truncate mt-1">
              {form.host ? `${form.host}:${form.port}` : "Henüz Tanımlanmadı"}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              {form.useSsl
                ? Number(form.port) === 465
                  ? "Implicit SSL/TLS Korumalı"
                  : "STARTTLS Şifreli Bağlantı"
                : "Şifresiz (Önerilmez)"}
            </p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
            <Server className="w-5 h-5" />
          </div>
        </div>

        {/* 3. Kimlik Doğrulama Kasası */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/80 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Kimlik Doğrulama & Kasa</span>
            <div className="text-base font-bold text-gray-900 dark:text-white mt-1">
              {hasPassword ? "Şifre Kayıtlı" : "Şifre Girilmedi"}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              {hasPassword ? "DataProtection (AES) ile korunuyor" : "Kimlik doğrulama için şifre girin"}
            </p>
          </div>
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center ${
              hasPassword
                ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
                : "bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
            }`}
          >
            <KeyRound className="w-5 h-5" />
          </div>
        </div>

        {/* 4. Otomatik Zamanlayıcı (MasterDB) */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/80 shadow-sm flex items-center justify-between gap-2">
          <div>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Otomatik Abonelik Servisi</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-base font-bold text-gray-900 dark:text-white">
                {sysStatus.isEnabled ? "Çalışıyor" : "Durduruldu"}
              </span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              {sysStatus.isEnabled
                ? "Zamanlanmış abonelikler aktif"
                : "Zamanlanmış abonelikler beklemede"}
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={toggleAutoSystem}
              disabled={sysLoading}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all shrink-0 ${
                sysStatus.isEnabled
                  ? "bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800"
                  : "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800"
              }`}
            >
              {sysLoading ? "..." : sysStatus.isEnabled ? "Durdur" : "Başlat"}
            </button>
          )}
        </div>
      </div>

      {/* Bildirim Bannerları */}
      {error && (
        <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/70 text-red-700 dark:text-red-300 text-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">İşlem Tamamlanamadı</div>
            <div className="text-xs mt-0.5 opacity-90">{error}</div>
          </div>
        </div>
      )}

      {success && (
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/70 text-emerald-800 dark:text-emerald-200 text-sm flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">Başarılı</div>
            <div className="text-xs mt-0.5 opacity-90">{success}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center text-gray-500">
          <Loader2 className="w-7 h-7 animate-spin text-blue-600 mb-3" />
          <span className="text-sm font-medium">Mail sistem ayarları yükleniyor...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
          {/* SOL KOLON: SMTP Yapılandırma Formu (7 Kolon) */}
          <form
            onSubmit={save}
            className="xl:col-span-7 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/80 shadow-sm overflow-hidden"
          >
            {/* Kart Başlığı + Aktif/Pasif Switch */}
            <div className="p-5 md:p-6 border-b border-gray-100 dark:border-gray-700/80 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gray-50/50 dark:bg-gray-800/50">
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Server className="w-4 h-4 text-blue-600" />
                  SMTP Sunucu ve Gönderici Yapılandırması
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Kurumsal SMTP bilgilerinizi girin veya aşağıdaki hazır sağlayıcı şablonlarından birini seçin.
                </p>
              </div>
              <div className="flex items-center gap-3 bg-white dark:bg-gray-900 px-3.5 py-2 rounded-xl border border-gray-200/80 dark:border-gray-700 self-start sm:self-auto">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                  SMTP Gönderimi Aktif
                </span>
                <ToggleSwitch
                  checked={Boolean(form.isEnabled)}
                  onChange={(v) => setField("isEnabled", v)}
                  disabled={!canEdit}
                  label="SMTP Gönderimi Aktif"
                />
              </div>
            </div>

            <div className="p-5 md:p-6 space-y-6">
              {/* Hızlı Sağlayıcı Şablonları */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2.5">
                  Hızlı Sağlayıcı Şablonları
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {SMTP_PRESETS.map((preset) => {
                    const isSelected = activePreset.id === preset.id;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        disabled={!canEdit}
                        onClick={() => applyPreset(preset)}
                        className={`text-left p-3 rounded-xl border transition-all ${
                          isSelected
                            ? "border-blue-500 bg-blue-50/70 dark:bg-blue-900/25 ring-2 ring-blue-500/15"
                            : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-900/40"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-xs font-bold text-gray-900 dark:text-white truncate">
                            {preset.label}
                          </span>
                          {isSelected && <Sparkles className="w-3.5 h-3.5 text-blue-600 shrink-0" />}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 font-mono mt-0.5 truncate">
                          {preset.subtitle}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {activePreset?.tip && (
                  <div className="mt-3 p-3 rounded-xl bg-blue-50/70 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/60 flex items-start gap-2.5 text-xs text-blue-800 dark:text-blue-200">
                    <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                    <span>{activePreset.tip}</span>
                  </div>
                )}
              </div>

              {/* Gönderen Adı & Gönderen E-posta */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Gönderen Görünen Adı
                  </label>
                  <div className="relative">
                    <input
                      className={inputCls}
                      value={form.fromName}
                      onChange={(e) => setField("fromName", e.target.value)}
                      disabled={!canEdit}
                      placeholder="Örn: XPass PDKS Bildirim"
                      maxLength={120}
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">
                    Alıcıların gelen kutusunda görünecek kurumsal başlık.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Gönderen E-posta Adresi (From) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      className={inputCls}
                      value={form.fromEmail}
                      onChange={(e) => setField("fromEmail", e.target.value)}
                      disabled={!canEdit}
                      placeholder="ik-bildirim@firmaniz.com"
                      maxLength={254}
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">
                    Çoğu SMTP sunucusu gönderen adresin kimlik doğrulama hesabıyla aynı olmasını zorunlu tutar.
                  </p>
                </div>
              </div>

              {/* Sunucu (Host) & Port Seçimi */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-7">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    SMTP Sunucu Adresi (Host) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Globe className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      ref={hostInputRef}
                      className={`${inputCls} pl-10 font-mono`}
                      value={form.host}
                      onChange={(e) => {
                        const val = e.target.value;
                        setField("host", val);
                        const matched = SMTP_PRESETS.find(
                          (p) => p.host && p.host.toLowerCase() === val.trim().toLowerCase()
                        );
                        setSelectedPresetId(matched ? matched.id : "custom");
                      }}
                      disabled={!canEdit}
                      placeholder="smtp.firmaniz.com"
                      maxLength={255}
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">
                    Bulut güvenliği gereği yalnızca dış erişime açık geçerli SMTP sunucuları kabul edilir.
                  </p>
                </div>

                <div className="md:col-span-5">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    SMTP Portu <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {ALLOWED_PORTS.map((item) => {
                      const active = Number(form.port) === item.port;
                      return (
                        <button
                          key={item.port}
                          type="button"
                          disabled={!canEdit}
                          onClick={() => {
                            setField("port", item.port);
                            setField("useSsl", item.ssl);
                          }}
                          className={`py-2 px-2 rounded-xl border text-center transition-all ${
                            active
                              ? "border-blue-600 bg-blue-600 text-white font-bold shadow-sm"
                              : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-blue-400"
                          }`}
                          title={item.badge}
                        >
                          <div className="text-xs font-mono">{item.label}</div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">
                    Seçili Port: <strong className="text-gray-600 dark:text-gray-300">{form.port}</strong>{" "}
                    ({Number(form.port) === 465 ? "Implicit SSL" : Number(form.port) === 587 ? "STARTTLS" : "SMTP"})
                  </p>
                </div>
              </div>

              {/* SSL/TLS Güvenlik Katmanı */}
              <div className="p-4 rounded-xl bg-gray-50/80 dark:bg-gray-900/50 border border-gray-200/70 dark:border-gray-700/80 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-100/80 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Lock className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-900 dark:text-white">
                      SSL / STARTTLS Şifreli İletişim
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      E-posta içeriklerinin ve kimlik bilgilerinin ağ üzerinde şifrelenerek iletilmesini sağlar (Önerilir).
                    </div>
                  </div>
                </div>
                <ToggleSwitch
                  checked={Boolean(form.useSsl)}
                  onChange={(v) => setField("useSsl", v)}
                  disabled={!canEdit}
                  label="SSL/TLS"
                />
              </div>

              {/* Kimlik Doğrulama (Kullanıcı Adı & Şifre) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    SMTP Kullanıcı Adı
                  </label>
                  <div className="relative">
                    <UserCheck className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      autoComplete="username"
                      className={`${inputCls} pl-10`}
                      value={form.username}
                      onChange={(e) => setField("username", e.target.value)}
                      disabled={!canEdit}
                      placeholder="ik-bildirim@firmaniz.com"
                      maxLength={254}
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">
                    Genellikle gönderen e-posta adresi ile aynıdır.
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                      Şifre / Uygulama Şifresi
                    </label>
                    {hasPassword ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                        <ShieldCheck className="w-3 h-3" /> Şifreli Kayıtlı
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                        Kayıtlı Değil
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="mailPassword"
                      autoComplete="new-password"
                      className={`${inputCls} pr-10`}
                      value={form.password}
                      onChange={(e) => setField("password", e.target.value)}
                      disabled={!canEdit}
                      placeholder={
                        hasPassword
                          ? "•••••••••••• (Değiştirmek için yeni şifre yazın)"
                          : "SMTP şifresi veya API anahtarı girin"
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      tabIndex={-1}
                      title={showPassword ? "Şifreyi Gizle" : "Şifreyi Göster"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">
                    Boş bırakırsanız mevcut kayıtlı şifre korunur. Şifreler ASP.NET DataProtection ile şifrelenir.
                  </p>
                </div>
              </div>

              {/* Alt Kaydet Barı */}
              <div className="pt-3 border-t border-gray-100 dark:border-gray-700/80 flex items-center justify-between gap-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Tüm yapılandırma değişiklikleri Güvenlik Denetim Loglarına kaydedilir.</span>
                </div>

                {canEdit && (
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs font-semibold shadow-sm shadow-blue-500/20 transition-all disabled:opacity-60 shrink-0"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Değişiklikleri Kaydet
                  </button>
                )}
              </div>
            </div>
          </form>

          {/* SAĞ KOLON: Bağlantı Kontrol Listesi & Canlı Test Gönderimi (5 Kolon) */}
          <div className="xl:col-span-5 space-y-6">
            {/* 1. Bağlantı Hazırlık Kontrol Listesi */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 border border-gray-100 dark:border-gray-700/80 shadow-sm">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-indigo-600" />
                Yapılandırma Hazırlık Kontrolü
              </h3>
              <div className="space-y-2.5">
                {checklist.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 p-2.5 rounded-xl bg-gray-50/80 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700/60"
                  >
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                        item.ok
                          ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400"
                          : "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400"
                      }`}
                    >
                      {item.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">{item.label}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{item.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. Canlı Test E-postası Gönderim Kartı */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 md:p-6 border border-gray-100 dark:border-gray-700/80 shadow-sm">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Send className="w-4 h-4 text-emerald-600" />
                  Canlı Bağlantı ve Test E-postası
                </h3>
                <span className="text-[11px] px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium">
                  15sn Zaman Aşımı Korumalı
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                SMTP ayarlarınızı canlıya almadan önce bile (sistem pasifken) kendi adresinize test e-postası göndererek bağlantıyı doğrulayabilirsiniz.
              </p>

              <form onSubmit={sendTest} className="space-y-3.5">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                      Test Alıcı E-posta Adresi <span className="text-red-500">*</span>
                    </label>
                    {(perms.userEmail || form.fromEmail) && (
                      <button
                        type="button"
                        onClick={() => setTestTo(perms.userEmail || form.fromEmail)}
                        className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {perms.userEmail ? "Kendi E-postamı Doldur" : "Gönderen Adresini Doldur"}
                      </button>
                    )}
                  </div>
                  <input
                    type="email"
                    className={inputCls}
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    disabled={!canEdit || testSending}
                    placeholder="ornek@firmaniz.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Test Konusu <span className="text-[11px] font-normal text-gray-400">(İsteğe Bağlı)</span>
                  </label>
                  <input
                    className={inputCls}
                    value={testSubject}
                    onChange={(e) => setTestSubject(e.target.value)}
                    disabled={!canEdit || testSending}
                    placeholder="XPass PDKS — SMTP Bağlantı Testi"
                    maxLength={150}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Test Notu <span className="text-[11px] font-normal text-gray-400">(İsteğe Bağlı)</span>
                  </label>
                  <textarea
                    rows={2}
                    className={inputCls}
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    disabled={!canEdit || testSending}
                    placeholder="Bu e-posta SMTP bağlantı doğrulaması için gönderilmiştir."
                    maxLength={500}
                  />
                </div>

                {isDirty && (
                  <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-800 dark:text-amber-200">
                    Formda kaydedilmemiş değişiklikleriniz var. <strong>Test Gönder</strong> butonuna bastığınızda önce güncel ayarlarınız otomatik kaydedilecek, ardından test maili gönderilecektir.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canEdit || testSending || saving}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm shadow-emerald-500/20 transition-all disabled:opacity-60"
                >
                  {testSending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      SMTP Sunucusuna Bağlanılıyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {isDirty ? "Ayarları Kaydet ve Test Maili Gönder" : "Test Maili Gönder"}
                    </>
                  )}
                </button>
              </form>

              {lastTestResult && (
                <div
                  className={`mt-4 p-3.5 rounded-xl border text-xs ${
                    lastTestResult.ok
                      ? "bg-emerald-50/70 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200"
                      : "bg-red-50/70 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
                  }`}
                >
                  <div className="flex items-center justify-between font-bold mb-1">
                    <span>{lastTestResult.ok ? "Son Test Başarılı" : "Son Test Başarısız"}</span>
                    <span className="text-[11px] opacity-75">{lastTestResult.time}</span>
                  </div>
                  <p className="leading-relaxed">{lastTestResult.message}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
